// MARK://dhk8/storefront/pages/ReservarPage
// Public unauthenticated reservation page (Fase 1 demo).
//
// URL: /reservar/:tenantId (Fase 1 acepta UUID directo; resolver por slug
// requiere cross-service lookup → Fase 2). Las reservas se mandan al endpoint
// público de dhk8-pos-floor: POST /v1/floor/public/reservations.
//
// El endpoint backend:
//   - Es rate-limited (5/IP/10min)
//   - Emite outbox event `dhk8.crm.contact.upsert_requested` (consumer en
//     dhk8-crm-contacts pendiente).
//   - Devuelve un DTO stripped (id, name, partySize, reservedAt, status).
//
// El API base es VITE_API_BASE (cae al patrón /v1 si no está seteado).
//
// STYLING: usa @dockethub/ui (data-brand="nintaigo") + clases sf-* definidas en
// src/styles.css. Sin inline styles, sin colores hardcoded — todo via tokens.

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import BrandHeader from '../components/BrandHeader.js'
import BusinessIdentity from '../components/BusinessIdentity.js'
import SiteFooter from '../components/SiteFooter.js'
import { useTenantBranding } from '../lib/branding.js'
import { resolveTenantSlug } from '../lib/tenant-slug.js'

const API_BASE =
  (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env['VITE_API_BASE'] ??
  '/v1'

function isUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
}

function todayISODate(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function ReservarPage() {
  // El parámetro acepta UUID directo o SLUG humano (reservar.nintaigo.com/reservar/<slug>).
  // Si es slug lo resolvemos a tenantId vía tenants/by-slug (mismo patrón que QueuePage).
  const { tenantId: param } = useParams<{ tenantId: string }>()
  const [resolvedId, setResolvedId] = useState<string | null>(param && isUuid(param) ? param : null)
  const [resolving, setResolving] = useState<boolean>(!!param && !isUuid(param))
  useEffect(() => {
    if (!param || isUuid(param)) return
    let cancelled = false
    setResolving(true)
    fetch(`${API_BASE}/dhk8-platform-tenants/v1/tenants/by-slug/${encodeURIComponent(param)}`, { headers: { accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((body: { ok?: boolean; data?: { tenantId?: string } } | null) => {
        if (cancelled) return
        setResolvedId(body && body.ok ? body.data?.tenantId ?? null : null)
        setResolving(false)
      })
      .catch(() => { if (!cancelled) { setResolvedId(null); setResolving(false) } })
    return () => { cancelled = true }
  }, [param])

  const tenantId = resolvedId
  useEffect(() => {
    const slug = param && !isUuid(param) ? param : null
    if (slug === null) return
    let vivo = true
    void (async () => {
      try {
        const r = await fetch(`${API_BASE}/dhk8-pos-floor/v1/floor/public/branches/${encodeURIComponent(slug)}`)
        if (!r.ok) return
        const b = (await r.json()) as { ok: boolean; data?: { items?: { branchId: string; name: string }[] } }
        if (!vivo || !b.ok) return
        const items = b.data?.items ?? []
        setSucursales(items)
        if (items.length > 0) setBranchId((prev) => (prev === '' ? items[0]!.branchId : prev))
      } catch { /* sin sucursales -> se reserva sin especificar */ }
    })()
    return () => { vivo = false }
  }, [param])
  const validTenant = !!tenantId && isUuid(tenantId)
  const branding = useTenantBranding(validTenant ? tenantId : null)
  const hasCustomBranding =
    branding.displayName && branding.displayName.trim().toLowerCase() !== 'nintaigo'

  useEffect(() => {
    document.title = `Reservar mesa${hasCustomBranding ? ` · ${branding.displayName}` : ''} — NintaiGO`
  }, [hasCustomBranding, branding.displayName])

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [partySize, setPartySize] = useState(2)
  const [date, setDate] = useState(todayISODate())
  const [time, setTime] = useState('19:30')
  const [notes, setNotes] = useState('')
  // ── Sucursal ─────────────────────────────────────────────────────────
  // Los enlaces publicos son del tenant; sin esto la reserva/turno nacia sin
  // sucursal y aparecia en todos los locales por igual.
  const [sucursales, setSucursales] = useState<{ branchId: string; name: string }[]>([])
  const [branchId, setBranchId] = useState<string>('')

  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [success, setSuccess] = useState<{
    name: string
    reservedAt: string
    reservationCode: string | null
    tenantSlug: string | null
  } | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validTenant) {
      setErr('URL inválida.')
      return
    }
    if (!name.trim() || !phone.trim()) {
      setErr('Nombre y teléfono son obligatorios.')
      return
    }
    setBusy(true)
    setErr(null)
    try {
      const reservedAt = new Date(`${date}T${time}:00`).toISOString()
      const body = {
        tenantId,
        name: name.trim(),
        phone: phone.trim(),
        partySize,
        reservedAt,
        ...(branchId ? { branchId } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }
      const res = await fetch(
        `${API_BASE}/dhk8-pos-floor/v1/floor/public/reservations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      )
      const data = (await res.json()) as
        | {
            ok: true
            data: { name: string; reservedAt: string; reservationCode?: string | null }
          }
        | { ok: false; error: { code: string; message: string } }
      if (!res.ok || !('ok' in data) || !data.ok) {
        const msg = !data.ok && 'error' in data ? data.error.message : `HTTP ${res.status}`
        throw new Error(msg)
      }
      // Resolvemos tenantSlug lazy para construir el deeplink de consulta.
      const tenantSlug = tenantId ? await resolveTenantSlug(tenantId) : null
      setSuccess({
        name: data.data.name,
        reservedAt: data.data.reservedAt,
        reservationCode: data.data.reservationCode ?? null,
        tenantSlug,
      })
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error al enviar la reserva')
    } finally {
      setBusy(false)
    }
  }

  if (resolving) {
    return (
      <div className="sf-page">
        <BrandHeader branding={branding} platformBrand />
        <div className="sf-form-page">
          <div className="sf-card">
            <p className="sf-card__lede">Cargando…</p>
          </div>
        </div>
      </div>
    )
  }

  if (!validTenant) {
    return (
      <div className="sf-page">
        <BrandHeader branding={branding} platformBrand />
        <div className="sf-form-page">
          <div className="sf-card">
            <h1 className="sf-card__title">URL inválida</h1>
            <p className="sf-card__lede">
              El enlace de reserva no es válido. Por favor verifica con el restaurante.
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    const formatted = new Date(success.reservedAt).toLocaleString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    })
    const consultaPath = success.tenantSlug
      ? `/consulta/${success.tenantSlug}`
      : null
    return (
      <div className="sf-page">
        <BrandHeader branding={branding} platformBrand />
        <div className="sf-form-page">
          <div className="sf-card">
            <BusinessIdentity branding={branding} />
            <h1 className="sf-card__title">¡Reserva confirmada!</h1>
            <p>
              Gracias, <strong>{success.name}</strong>. Tu mesa quedó apartada
              {hasCustomBranding ? <> en <strong>{branding.displayName}</strong></> : null}
              {' '}para:
            </p>
            <p className="sf-card__highlight">{formatted}</p>
            {success.reservationCode ? (
              <div className="sf-code-block" data-testid="reservation-code-block">
                <span className="sf-code-block__label">Tu código de reserva</span>
                <span className="sf-code-block__value">{success.reservationCode}</span>
                <p className="sf-code-block__hint">
                  Guárdalo. Puedes consultar tu reserva en cualquier momento desde
                  {' '}
                  {consultaPath ? (
                    <a
                      href={consultaPath}
                      className="sf-code-block__link"
                    >
                      reservar.nintaigo.com{consultaPath}
                    </a>
                  ) : (
                    <span>la página de consulta del restaurante</span>
                  )}
                  {' '}
                  ingresando el código <strong>{success.reservationCode}</strong>.
                </p>
              </div>
            ) : null}
            <p className="sf-card__meta">
              Te esperamos. Si necesitas modificar o cancelar, contacta al restaurante.
            </p>
          </div>
        </div>
        <SiteFooter />
      </div>
    )
  }

  return (
    <div className="sf-page">
      <BrandHeader branding={branding} platformBrand />
      <div className="sf-form-page">
        <form className="sf-card" onSubmit={(e) => void handleSubmit(e)}>
          <BusinessIdentity branding={branding} />
          <h1 className="sf-card__title">Reservar mesa</h1>
          <p className="sf-card__lede">
            {hasCustomBranding ? <>Reserva en <strong>{branding.displayName}</strong>. </> : null}
            Completa tus datos para apartar tu mesa.
          </p>

        {sucursales.length > 1 && (
          <label className="sf-field">
            <span className="sf-field__label">Sucursal</span>
            <select className="sf-input" value={branchId} onChange={(e) => setBranchId(e.target.value)} required>
              {sucursales.map((b) => <option key={b.branchId} value={b.branchId}>{b.name}</option>)}
            </select>
          </label>
        )}
        <label className="sf-field">
          <span className="sf-field__label">Nombre completo</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="sf-input"
            autoFocus
          />
        </label>
        <label className="sf-field">
          <span className="sf-field__label">Teléfono (WhatsApp)</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            placeholder="+52..."
            inputMode="tel"
            pattern="[+0-9 ()\-]+"
            className="sf-input"
          />
        </label>
        <div className="sf-row-3">
          <label className="sf-field">
            <span className="sf-field__label">Personas</span>
            <input
              type="number"
              min={1}
              max={50}
              value={partySize}
              onChange={(e) => setPartySize(Math.max(1, Number(e.target.value) || 1))}
              className="sf-input"
            />
          </label>
          <label className="sf-field">
            <span className="sf-field__label">Fecha</span>
            <input
              type="date"
              value={date}
              min={todayISODate()}
              onChange={(e) => setDate(e.target.value)}
              className="sf-input"
            />
          </label>
          <label className="sf-field">
            <span className="sf-field__label">Hora</span>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="sf-input"
            />
          </label>
        </div>
        <label className="sf-field">
          <span className="sf-field__label">Notas (opcional)</span>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Cumpleaños, alergias, etc."
            className="sf-input"
          />
        </label>
        {err && <div className="sf-error">{err}</div>}
        <button type="submit" disabled={busy} className="sf-btn-primary">
          {busy ? 'Enviando…' : 'Reservar'}
        </button>
          <p className="sf-footnote">
            Esta página es pública. Tus datos se usan solo para la reserva.
          </p>
        </form>
      </div>
      <SiteFooter />
    </div>
  )
}
